import React, { useState } from "react";
import { Modal, Box } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PauseIcon from "@mui/icons-material/Pause";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import styles from "./InterviewReport.module.scss";

interface ActionModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (notes: string) => void;
  action: "hold" | "reject" | "advance";
  candidateName: string;
}

const ActionModal: React.FC<ActionModalProps> = ({
  open,
  onClose,
  onConfirm,
  action,
  candidateName,
}) => {
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const getActionConfig = () => {
    switch (action) {
      case "hold":
        return {
          title: "Hold Candidate",
          icon: <PauseIcon className={styles.actionIconHold} />,
          color: styles.actionHold,
          description: `Are you sure you want to place ${candidateName} on hold?`,
          placeholder: "Add notes about why this candidate is being held...",
          confirmText: "Hold Candidate",
          confirmClass: styles.btnHold,
        };
      case "reject":
        return {
          title: "Reject Candidate",
          icon: <ErrorIcon className={styles.actionIconReject} />,
          color: styles.actionReject,
          description: `Are you sure you want to reject ${candidateName}?`,
          placeholder: "Add notes about the rejection reason...",
          confirmText: "Reject Candidate",
          confirmClass: styles.btnReject,
        };
      case "advance":
        return {
          title: "Advance Candidate",
          icon: <CheckCircleIcon className={styles.actionIconAdvance} />,
          color: styles.actionAdvance,
          description: `Are you sure you want to advance ${candidateName} to the next round?`,
          placeholder: "Add notes about why this candidate should advance...",
          confirmText: "Advance Candidate",
          confirmClass: styles.btnAdvance,
        };
    }
  };

  const config = getActionConfig();

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      await onConfirm(notes);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box className={styles.modal}>
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>{config.title}</h2>
          <button
            className={styles.modalClose}
            onClick={onClose}
            disabled={submitting}
          >
            <CloseIcon />
          </button>
        </div>
        <div className={styles.modalBody}>
          <div className={`${styles.actionMessage} ${config.color}`}>
            {config.icon}
            <p>{config.description}</p>
          </div>
          <label className={styles.formLabel}>
            Notes {action === "advance" ? "(Optional)" : "(Required)"}
            <textarea
              className={styles.formTextarea}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              placeholder={config.placeholder}
              disabled={submitting}
            />
          </label>
        </div>
        <div className={styles.modalFooter}>
          <button
            className={styles.btnSecondary}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className={config.confirmClass}
            onClick={handleConfirm}
            disabled={submitting || (action !== "advance" && !notes.trim())}
          >
            {submitting ? "Processing..." : config.confirmText}
          </button>
        </div>
      </Box>
    </Modal>
  );
};

export default ActionModal;
